import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./add.css";

const AgentSubmitProperty = () => {
  const navigate = useNavigate();
  const [agentInfo, setAgentInfo] = useState(null);

  const initialState = {
    title: "",
    price: "",
    transactionType: "sale",
    productType: "appartement",
    propertyCategory: "residential",
    coduStatus: "",
    address: "",
    city: "",
    coordinates: {
      latitude: "",
      longitude: "",
    },
    features: {
      bedrooms: 0,
      bathrooms: 0,
      parkingSpaces: 0,
      garages: 0,
      area: 0,
      hasPool: false,
      isWheelchairAccessible: false,
      isWaterfront: false,
      hasNavigableWater: false,
      allowsPets: false,
      allowsSmoking: false,
    },
    images: [],
    buildingDetails: {
      yearBuilt: "",
      isNewConstruction: false,
      isHistorical: false,
      structureType: [],
    },
    isVirtualTourAvailable: false,
    isOpenHouse: false,
    lotSize: 0,
    description: "",
    moveInDate: "",
    isForeclosure: false,
    // L'agent sera automatiquement assigné depuis le token
  };

  const [formData, setFormData] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Récupérer les infos de l'agent connecté
  useEffect(() => {
    const fetchAgentInfo = async () => {
      try {
        const token = localStorage.getItem('agentToken');
        if (!token) {
          navigate('/loginagent');
          return;
        }

        const response = await axios.get('https://keurgui-backend-kjour.ondigitalocean.app/api/agent/profile', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setAgentInfo(response.data);
      } catch (error) {
        console.error('Erreur récupération agent:', error);
        navigate('/loginagent');
      }
    };

    fetchAgentInfo();
  }, [navigate]);

  // 🔹 Gestion des champs standards
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // 🔹 Gestion des coordonnées
  const handleCoordinatesChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      coordinates: {
        ...prevState.coordinates,
        [name]: value ? parseFloat(value) : "",
      },
    }));
  };

  // 🔹 Gestion des champs features
  const handleFeatureChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      features: {
        ...prevState.features,
        [name]: type === 'checkbox' ? checked : (value ? parseInt(value, 10) : 0),
      },
    }));
  };

  // 🔹 Gestion des buildingDetails
  const handleBuildingDetailChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      buildingDetails: {
        ...prevState.buildingDetails,
        [name]: type === 'checkbox' ? checked : value,
      },
    }));
  };

  // 🔹 Gestion des structureType (checkboxes multiples)
  const handleStructureTypeChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      buildingDetails: {
        ...prevState.buildingDetails,
        structureType: checked
          ? [...prevState.buildingDetails.structureType, value]
          : prevState.buildingDetails.structureType.filter(item => item !== value)
      }
    }));
  };

  // 🔹 Upload d'image
  const handleLocalUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formDataFile = new FormData();
    formDataFile.append('image', file);

    try {
      const res = await axios.post('https://keurgui-backend-kjour.ondigitalocean.app/api/upload', formDataFile, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = res.data.imageUrl;
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, imageUrl]
      }));
    } catch (err) {
      console.error('Erreur d\'upload :', err);
      alert('Upload échoué');
    } finally {
      setUploadingImage(false);
    }
  };

  // 🔹 Supprimer une image
  const handleRemoveImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // 🔹 Validation du formulaire
  const validateForm = () => {
    const required = [
      'title', 'price', 'transactionType', 'productType', 
      'propertyCategory', 'coduStatus', 'address', 'city',
      'description'
    ];

    for (let field of required) {
      if (!formData[field]) {
        return `Le champ ${field} est requis`;
      }
    }

    if (!formData.coordinates.latitude || !formData.coordinates.longitude) {
      return 'Les coordonnées GPS sont requises';
    }

    if (formData.images.length === 0) {
      return 'Au moins une image est requise';
    }

    if (formData.price <= 0) {
      return 'Le prix doit être supérieur à 0';
    }

    return null;
  };

  // 🔹 Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const validationError = validateForm();
    if (validationError) {
      setSubmissionStatus({
        type: 'error',
        message: validationError
      });
      return;
    }

    setLoading(true);
    setSubmissionStatus(null);

    try {
      const token = localStorage.getItem('agentToken');
      
      // L'agent est automatiquement assigné depuis le token
      const response = await axios.post(
        "https://keurgui-backend-kjour.ondigitalocean.app/api/agent/submit-property",
        formData,
        { 
          headers: { 
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json" 
          } 
        }
      );

      setSubmissionStatus({
        type: 'success',
        message: '✅ Propriété soumise avec succès ! Elle sera publiée après validation par l\'administrateur.'
      });

      // Réinitialiser le formulaire après 3 secondes
      setTimeout(() => {
        setFormData(initialState);
        setSubmissionStatus(null);
      }, 3000);

    } catch (error) {
      console.error("Erreur:", error);
      setSubmissionStatus({
        type: 'error',
        message: error.response?.data?.message || '❌ Erreur lors de la soumission'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!agentInfo) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="agent-property-form">
      <div className="form-header">
        <h2>Soumettre une propriété</h2>
        <div className="agent-badge">
          <img src={agentInfo.photoProfil} alt={agentInfo.agentName} />
          <span>{agentInfo.agentName}</span>
        </div>
      </div>

      {submissionStatus && (
        <div className={`alert ${submissionStatus.type}`}>
          {submissionStatus.message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="property-form">
        {/* Informations de base */}
        <div className="form-section">
          <h3>Informations générales</h3>
          
          <div className="form-group">
            <label>Titre de l'annonce *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Ex: Bel appartement meublé à Dakar"
              required
              maxLength="100"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Prix (FCFA) *</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="Ex: 50000000"
                required
                min="0"
              />
            </div>

            <div className="form-group">
              <label>Surface (m²)</label>
              <input
                type="number"
                name="area"
                value={formData.features.area}
                onChange={handleFeatureChange}
                placeholder="Ex: 150"
                min="0"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Type de transaction *</label>
              <select
                name="transactionType"
                value={formData.transactionType}
                onChange={handleChange}
                required
              >
                <option value="sale">À vendre</option>
                <option value="rent">À louer</option>
                <option value="buy">Recherche à acheter</option>
              </select>
            </div>

            <div className="form-group">
              <label>Type de bien *</label>
              <select
                name="productType"
                value={formData.productType}
                onChange={handleChange}
                required
              >
                <option value="appartement">Appartement</option>
                <option value="bureau_commerce">Bureau / Commerce</option>
                <option value="hotel_restaurant">Hôtel / Restaurant</option>
                <option value="immeuble">Immeuble</option>
                <option value="residence">Résidence</option>
                <option value="studio_chambre">Studio / Chambre</option>
                <option value="villa_maison">Villa / Maison</option>
                <option value="terrain">Terrain</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Catégorie *</label>
              <select
                name="propertyCategory"
                value={formData.propertyCategory}
                onChange={handleChange}
                required
              >
                <option value="residential">Résidentiel</option>
                <option value="commercial">Commercial</option>
              </select>
            </div>

            <div className="form-group">
              <label>Statut CODOU *</label>
              <select
                name="coduStatus"
                value={formData.coduStatus}
                onChange={handleChange}
                required
              >
                <option value="">Sélectionnez</option>
                <option value="codu">CODU</option>
                <option value="noncodu">Non CODU</option>
              </select>
            </div>
          </div>
        </div>

        {/* Localisation */}
        <div className="form-section">
          <h3>Localisation</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Région *</label>
              <select
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
              >
                <option value="">Sélectionnez une région</option>
                <option value="Dakar">Dakar</option>
                <option value="Diourbel">Diourbel</option>
                <option value="Fatick">Fatick</option>
                <option value="Kaffrine">Kaffrine</option>
                <option value="Kaolack">Kaolack</option>
                <option value="Kédougou">Kédougou</option>
                <option value="Kolda">Kolda</option>
                <option value="Louga">Louga</option>
                <option value="Matam">Matam</option>
                <option value="Saint-Louis">Saint-Louis</option>
                <option value="Sédhiou">Sédhiou</option>
                <option value="Tambacounda">Tambacounda</option>
                <option value="Thiès">Thiès</option>
                <option value="Ziguinchor">Ziguinchor</option>
              </select>
            </div>

            <div className="form-group">
              <label>Adresse *</label>
              <select
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
              >
                <option value="">Sélectionnez une adresse</option>
                <option value="Dakar/Dakar(Gueule tapee fass colobane)">Dakar (Gueule Tapée/Fass/Colobane)</option>
                <option value="Dakar/Dakar(Fann point e amitie)">Dakar (Fann/Point E/Amitié)</option>
                <option value="Dakar/Dakar(Goree)">Dakar (Gorée)</option>
                <option value="Dakar/Dakar(Medina)">Dakar (Médina)</option>
                <option value="Dakar/Dakar(Plateau)">Dakar (Plateau)</option>
                <option value="Dakar/Dakar(Camberene)">Dakar (Cambérène)</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Latitude *</label>
              <input
                type="number"
                step="any"
                name="latitude"
                value={formData.coordinates.latitude}
                onChange={handleCoordinatesChange}
                placeholder="Ex: 14.69278"
                required
              />
            </div>

            <div className="form-group">
              <label>Longitude *</label>
              <input
                type="number"
                step="any"
                name="longitude"
                value={formData.coordinates.longitude}
                onChange={handleCoordinatesChange}
                placeholder="Ex: -17.44639"
                required
              />
            </div>
          </div>
        </div>

        {/* Caractéristiques */}
        <div className="form-section">
          <h3>Caractéristiques</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Nombre de chambres</label>
              <input
                type="number"
                name="bedrooms"
                value={formData.features.bedrooms}
                onChange={handleFeatureChange}
                min="0"
              />
            </div>

            <div className="form-group">
              <label>Salles de bain</label>
              <input
                type="number"
                name="bathrooms"
                value={formData.features.bathrooms}
                onChange={handleFeatureChange}
                min="0"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Places de parking</label>
              <input
                type="number"
                name="parkingSpaces"
                value={formData.features.parkingSpaces}
                onChange={handleFeatureChange}
                min="0"
              />
            </div>

            <div className="form-group">
              <label>Garages</label>
              <input
                type="number"
                name="garages"
                value={formData.features.garages}
                onChange={handleFeatureChange}
                min="0"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Superficie du terrain (m²)</label>
            <input
              type="number"
              name="lotSize"
              value={formData.lotSize}
              onChange={handleChange}
              min="0"
            />
          </div>

          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                name="hasPool"
                checked={formData.features.hasPool}
                onChange={handleFeatureChange}
              />
              Piscine
            </label>

            <label>
              <input
                type="checkbox"
                name="isWheelchairAccessible"
                checked={formData.features.isWheelchairAccessible}
                onChange={handleFeatureChange}
              />
              Accès handicapés
            </label>

            <label>
              <input
                type="checkbox"
                name="isWaterfront"
                checked={formData.features.isWaterfront}
                onChange={handleFeatureChange}
              />
              Bord de mer
            </label>

            <label>
              <input
                type="checkbox"
                name="allowsPets"
                checked={formData.features.allowsPets}
                onChange={handleFeatureChange}
              />
              Animaux acceptés
            </label>

            <label>
              <input
                type="checkbox"
                name="allowsSmoking"
                checked={formData.features.allowsSmoking}
                onChange={handleFeatureChange}
              />
              Fumeurs acceptés
            </label>
          </div>
        </div>

        {/* Détails du bâtiment */}
        <div className="form-section">
          <h3>Détails du bâtiment</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Année de construction</label>
              <input
                type="number"
                name="yearBuilt"
                value={formData.buildingDetails.yearBuilt}
                onChange={handleBuildingDetailChange}
                min="1800"
                max={new Date().getFullYear()}
              />
            </div>
          </div>

          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isNewConstruction"
                checked={formData.buildingDetails.isNewConstruction}
                onChange={handleBuildingDetailChange}
              />
              Nouvelle construction
            </label>

            <label>
              <input
                type="checkbox"
                name="isHistorical"
                checked={formData.buildingDetails.isHistorical}
                onChange={handleBuildingDetailChange}
              />
              Bâtiment historique
            </label>
          </div>

          <div className="form-group">
            <label>Type de structure (options multiples)</label>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  value="Bord de l'eau"
                  checked={formData.buildingDetails.structureType.includes("Bord de l'eau")}
                  onChange={handleStructureTypeChange}
                />
                Bord de l'eau
              </label>
              <label>
                <input
                  type="checkbox"
                  value="Accès à l'eau"
                  checked={formData.buildingDetails.structureType.includes("Accès à l'eau")}
                  onChange={handleStructureTypeChange}
                />
                Accès à l'eau
              </label>
              <label>
                <input
                  type="checkbox"
                  value="Plan d'eau navigable"
                  checked={formData.buildingDetails.structureType.includes("Plan d'eau navigable")}
                  onChange={handleStructureTypeChange}
                />
                Plan d'eau navigable
              </label>
              <label>
                <input
                  type="checkbox"
                  value="Villégiature"
                  checked={formData.buildingDetails.structureType.includes("Villégiature")}
                  onChange={handleStructureTypeChange}
                />
                Villégiature
              </label>
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="form-section">
          <h3>Images *</h3>
          
          <div className="image-upload">
            <input
              type="file"
              accept="image/*"
              onChange={handleLocalUpload}
              disabled={uploadingImage}
            />
            {uploadingImage && <span>Téléchargement...</span>}
          </div>

          <div className="image-preview">
            {formData.images.map((imgUrl, index) => (
              <div key={index} className="image-item">
                <img src={imgUrl} alt={`Aperçu ${index + 1}`} />
                <button 
                  type="button" 
                  onClick={() => handleRemoveImage(index)}
                  className="remove-image"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Description */}
        <div className="form-section">
          <h3>Description *</h3>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="6"
            placeholder="Décrivez votre bien en détail (localisation, commodités à proximité, état général, etc.)"
            required
          />
        </div>

        {/* Options supplémentaires */}
        <div className="form-section">
          <h3>Options supplémentaires</h3>

          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isVirtualTourAvailable"
                checked={formData.isVirtualTourAvailable}
                onChange={handleChange}
              />
              Visite virtuelle disponible
            </label>

            <label>
              <input
                type="checkbox"
                name="isOpenHouse"
                checked={formData.isOpenHouse}
                onChange={handleChange}
              />
              Portes ouvertes
            </label>

            <label>
              <input
                type="checkbox"
                name="isForeclosure"
                checked={formData.isForeclosure}
                onChange={handleChange}
              />
              Saisie immobilière
            </label>
          </div>

          <div className="form-group">
            <label>Date d'emménagement possible</label>
            <input
              type="date"
              name="moveInDate"
              value={formData.moveInDate}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>

        {/* Bouton de soumission */}
        <div className="form-actions">
          <button 
            type="submit" 
            disabled={loading}
            className="submit-btn"
          >
            {loading ? 'Soumission en cours...' : 'Soumettre pour approbation'}
          </button>
          
          <p className="info-note">
            <span className="info-icon">ℹ️</span>
            Votre propriété sera vérifiée par l'administrateur avant publication
          </p>
        </div>
      </form>
    </div>
  );
};

export default AgentSubmitProperty;